import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../components/SubscriptionGuard';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Slider } from '../../components/ui/slider';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  InviteDialog, BulkInviteDialog, EditEmployeeDialog, 
  DeactivateDialog, DeleteEmployeeDialog, DepartmentDialog, CreateEmployeeDialog, ImportEmployeesDialog 
} from '../../components/admin/PeopleDialogs';
import { format } from 'date-fns';
import { 
  Users, 
  UserPlus, 
  Search, 
  MoreHorizontal, 
  Mail, 
  UserX, 
  UserCheck,
  Edit,
  ChevronUp,
  ChevronDown,
  Upload,
  Download,
  Building2,
  Plus,
  Trash2,
  Clock,
  RefreshCw,
  X,
  Activity,
  Filter,
  SlidersHorizontal,
  AlertTriangle,
  KeyRound,
  Smartphone
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' }
];

// Helper: Format date as "DD Mon YYYY"
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd MMM yyyy');
  } catch {
    return '-';
  }
};

export default function PeopleManagement() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const isSubscriptionExpired = subscription?.has_subscription && !subscription?.is_valid && !subscription?.is_internal;
  const isFreePlan = subscription?.plan === 'free';
  
  // State
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, total_pages: 1 });
  const [stats, setStats] = useState({ total_employees: 0, active_now: 0, pending_invites: 0, departments: 0 });
  const [invitations, setInvitations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modals
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [createEmployeeModalOpen, setCreateEmployeeModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [trackingPolicy, setTrackingPolicy] = useState('standard');
  const isFullStealthPolicy = trackingPolicy === 'full_stealth';
  
  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [inviteForm, setInviteForm] = useState({ emails: [], role: 'employee', department: '', personal_message: '' });
  const [bulkInvites, setBulkInvites] = useState([]);
  const [departmentForm, setDepartmentForm] = useState({ name: '', description: '' });
  const [departmentFormError, setDepartmentFormError] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [createEmployeeForm, setCreateEmployeeForm] = useState({ 
    name: '', email: '', role: 'employee', department: '', manager_id: '', temporary_password: '' 
  });
  const [createEmployeeLoading, setCreateEmployeeLoading] = useState(false);
  const [orgLeaveTypes, setOrgLeaveTypes] = useState([]);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [stealthEnabled, setStealthEnabled] = useState(false);
  const [deviceTokenModal, setDeviceTokenModal] = useState(null); // { token, employee_name, expires_at }
  
  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchInput]);

  // Fetch data
  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetch(`${API_URL}/api/admin/people?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEmployees(data.employees || []);
      setPagination(data.pagination || pagination);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [token, pagination.page, pagination.limit, searchQuery, roleFilter, departmentFilter, statusFilter, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/people/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // Ensure we always set an array
      setInvitations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      setInvitations([]);
    }
  }, [token]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  }, [token]);

  const initialLoadRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchStats(), fetchInvitations(), fetchDepartments()]);
      // Fetch org feature flags to check stealth_mode
      try {
        const flagsRes = await fetch(`${API_URL}/api/features`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (flagsRes.ok) {
          const flags = await flagsRes.json();
          setStealthEnabled(!!flags.stealth_mode);
        }
      } catch (e) { console.log('Could not fetch feature flags'); }
      // Fetch workspace tracking policy (full_stealth disables email invitations)
      try {
        const res = await fetch(`${API_URL}/api/workspace-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTrackingPolicy(data.tracking_policy || 'standard');
        }
      } catch { /* ignore */ }
      // Fetch org leave types
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/time-off/leave-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrgLeaveTypes(data);
        }
      } catch (e) { console.log('Could not fetch leave types'); }
      setLoading(false);
      initialLoadRef.current = true;
    };
    if (token) loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Re-fetch employees only when search/filter/sort changes (after initial load)
  useEffect(() => {
    if (initialLoadRef.current && token) {
      fetchEmployees();
    }
  }, [searchQuery, roleFilter, departmentFilter, statusFilter, sortBy, sortOrder, fetchEmployees, token]);

  // Handlers
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.emails || inviteForm.emails.length === 0) {
      toast.error('At least one email is required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/invitations/bulk`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emails: inviteForm.emails,
          role: inviteForm.role,
          department: inviteForm.department,
          personal_message: inviteForm.personal_message
        })
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.seats) {
          toast.error(`${data.detail}`, { duration: 8000 });
        } else {
          throw new Error(data.detail || 'Failed to send invitations');
        }
        return;
      }

      const successCount = data.results?.filter(r => r.success).length || 0;
      const failCount = data.results?.filter(r => !r.success).length || 0;

      if (successCount > 0) toast.success(`${successCount} invitation${successCount > 1 ? 's' : ''} sent successfully`);
      if (failCount > 0) {
        const failedEmails = data.results.filter(r => !r.success).map(r => `${r.email}: ${r.error}`).join('\n');
        toast.error(`${failCount} failed:\n${failedEmails}`);
      }

      setInviteModalOpen(false);
      setInviteForm({ emails: [], role: 'employee', department: '', personal_message: '' });
      fetchInvitations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Create employee directly (bypasses email)
  const handleCreateEmployee = async () => {
    if (isFreePlan) {
      toast.error('Individual plan does not support team members. Please upgrade to Business Plan.');
      setCreateEmployeeModalOpen(false);
      return;
    }

    if (!createEmployeeForm.name || !createEmployeeForm.email) {
      toast.error('Name and email are required');
      return;
    }

    if (/\d/.test(createEmployeeForm.name)) {
      toast.error('Full name should only contain letters, no numbers');
      return;
    }

    // In Full Stealth policy, employees don't have web login → auto-generate a random unused password.
    // Otherwise enforce the admin-entered password.
    const payload = { ...createEmployeeForm };
    if (isFullStealthPolicy) {
      payload.temporary_password = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2) + 'A1!';
    } else if (!createEmployeeForm.temporary_password) {
      toast.error('Temporary password is required');
      return;
    } else if (createEmployeeForm.temporary_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreateEmployeeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/people/create-direct`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create employee');
      }

      const data = await res.json();
      toast.success(`Employee "${createEmployeeForm.name}" created!`, {
        description: 'Status: Pending Setup. Review their profile, then send an invitation when ready.'
      });
      setCreateEmployeeModalOpen(false);
      setCreateEmployeeForm({ name: '', email: '', role: 'employee', department: '', manager_id: '', temporary_password: '' });
      setStatusFilter('all');
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreateEmployeeLoading(false);
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCreateEmployeeForm(prev => ({ ...prev, temporary_password: password }));
  };

  const handleBulkInviteUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      const invites = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const invite = {};
        headers.forEach((header, idx) => {
          if (header === 'name') invite.name = values[idx] || '';
          if (header === 'email') invite.email = values[idx] || '';
          if (header === 'role') invite.role = values[idx]?.toLowerCase() || 'employee';
          if (header === 'department') invite.department = values[idx] || '';
        });
        if (invite.email) invites.push(invite);
      }
      setBulkInvites(invites);
    };
    reader.readAsText(file);
  };

  const handleBulkInvite = async () => {
    if (bulkInvites.length === 0) {
      toast.error('No invitations to send');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/invitations/bulk`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invitations: bulkInvites })
      });

      const data = await res.json();
      toast.success(`${data.success?.length || 0} invitations sent, ${data.failed?.length || 0} failed`);
      setBulkInviteModalOpen(false);
      setBulkInvites([]);
      fetchInvitations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to send bulk invitations');
    }
  };

  const downloadCsvTemplate = () => {
    const csv = 'Name,Email,Role,Department\nJohn Doe,john@example.com,employee,Engineering\nJane Smith,jane@example.com,manager,Marketing';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invite_template.csv';
    a.click();
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await fetch(`${API_URL}/api/admin/invitations/${inviteId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation resent');
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await fetch(`${API_URL}/api/admin/invitations/${inviteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation cancelled');
      fetchInvitations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const openEditModal = async (employeeId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/people/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedEmployee(data);
      setEditModalOpen(true);
    } catch (error) {
      toast.error('Failed to load employee details');
    }
  };

  const handleSaveEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/people/${selectedEmployee.id}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectedEmployee.name,
          email: selectedEmployee.email,
          job_title: selectedEmployee.job_title,
          department: selectedEmployee.department,
          role: selectedEmployee.role,
          manager_id: selectedEmployee.manager_id,
          timezone: selectedEmployee.timezone,
          working_days: selectedEmployee.working_days,
          daily_hours_target: selectedEmployee.daily_hours_target,
          account_status: selectedEmployee.account_status,
          leave_balances: selectedEmployee.leave_balances,
          tracking_mode: selectedEmployee.tracking_mode,
          stealth_screenshot_interval: selectedEmployee.stealth_screenshot_interval,
          stealth_idle_timeout: selectedEmployee.stealth_idle_timeout
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save');
      }

      toast.success('Employee updated successfully');
      setEditModalOpen(false);
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleGenerateDeviceToken = async (emp) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/people/${emp.id}/device-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeviceTokenModal({
          token: data.device_token,
          employee_name: data.employee_name,
          employee_email: data.employee_email,
          expires_at: data.expires_at
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate token');
      }
    } catch { toast.error('Failed to generate device token'); }
  };


  const handleDeactivate = async () => {
    if (!selectedEmployee) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/people/${selectedEmployee.id}/deactivate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to deactivate');
      }

      toast.success('Employee deactivated');
      setDeactivateModalOpen(false);
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/people/${selectedEmployee.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete employee');
      }
      toast.success('Employee deleted permanently');
      setDeleteModalOpen(false);
      setSelectedEmployee(null);
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleReactivate = async (employeeId) => {
    try {
      await fetch(`${API_URL}/api/admin/people/${employeeId}/reactivate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee reactivated');
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  const [sendingInvitation, setSendingInvitation] = useState(null);
  const [bulkSendingInvitations, setBulkSendingInvitations] = useState(false);

  const handleSendInvitation = async (employeeId) => {
    setSendingInvitation(employeeId);
    try {
      const res = await fetch(`${API_URL}/api/admin/people/${employeeId}/send-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send invitation');
      
      if (data.email_sent) {
        toast.success('Setup link sent! Employee will receive an email to set their password.');
      } else {
        toast.info(`Setup link generated but email failed. Share manually: ${data.setup_url}`);
      }
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleBulkSendInvitations = async () => {
    setBulkSendingInvitations(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/people/bulk-send-invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send invitations');
      toast.success(`${data.sent} invitation(s) sent successfully!`);
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkSendingInvitations(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedEmployee || !resetPasswordForm || resetPasswordForm.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetPasswordLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/people/${selectedEmployee.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_password: resetPasswordForm })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to reset password');
      }
      toast.success(`Password reset for ${selectedEmployee.name}. They will be prompted to change it on next login.`);
      setResetPasswordModalOpen(false);
      setResetPasswordForm('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const generateResetPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let password = '';
    for (let i = 0; i < 10; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));
    setResetPasswordForm(password);
  };

  const handleSaveDepartment = async () => {
    if (!departmentForm.name.trim()) {
      setDepartmentFormError('Department name is required');
      return;
    }
    if (/\d/.test(departmentForm.name)) {
      setDepartmentFormError('Department name should only contain letters and characters, no numbers');
      return;
    }
    setDepartmentFormError('');

    try {
      const url = editingDepartment 
        ? `${API_URL}/api/admin/departments/${editingDepartment.id}`
        : `${API_URL}/api/admin/departments`;
      
      const res = await fetch(url, {
        method: editingDepartment ? 'PUT' : 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(departmentForm)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save department');
      }

      toast.success(editingDepartment ? 'Department updated successfully' : 'Department created successfully');
      setDepartmentModalOpen(false);
      setDepartmentForm({ name: '', description: '' });
      setDepartmentFormError('');
      setEditingDepartment(null);
      fetchDepartments();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!confirm('Are you sure you want to delete this department? Employees will be unassigned.')) return;

    try {
      await fetch(`${API_URL}/api/admin/departments/${deptId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Department deleted');
      fetchDepartments();
      fetchStats();
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  // Helpers
  const getLastActiveLabel = (lastTracked) => {
    if (!lastTracked) return { text: 'Never', color: 'text-slate-400' };
    
    const now = new Date();
    const tracked = new Date(lastTracked);
    const diffMs = now - tracked;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return { text: 'Today', color: 'text-emerald-500' };
    if (diffDays === 1) return { text: '1d ago', color: 'text-amber-500' };
    if (diffDays <= 7) return { text: `${diffDays}d ago`, color: 'text-amber-500' };
    return { text: `${diffDays}d ago`, color: 'text-red-500' };
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300';
      case 'manager': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-600/30 dark:text-slate-300';
    }
  };

  const SortHeader = ({ column, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="people-management-page">

      {/* Subscription Expired Banner */}
      {isSubscriptionExpired && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="expired-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Subscription expired</p>
              <p className="text-xs text-amber-700 dark:text-amber-400">You can deactivate users to adjust your team size, then subscribe to continue.</p>
            </div>
          </div>
          <Button size="sm" onClick={() => navigate('/billing')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg whitespace-nowrap" data-testid="banner-subscribe-btn">
            Subscribe Now
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('nav.people')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('people.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setDepartmentModalOpen(true)}
            data-testid="manage-departments-btn"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Departments
          </Button>
          
          {/* Create Employee Directly - Admin only */}
          {user?.role === 'admin' && (
            <>
              <Button 
                variant="outline"
                onClick={() => isSubscriptionExpired ? toast.error('Subscribe to import employees') : setImportModalOpen(true)}
                disabled={isSubscriptionExpired}
                data-testid="import-employees-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              {employees.some(e => e.account_status === 'pending_setup') && !isFullStealthPolicy && (
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={handleBulkSendInvitations}
                  disabled={bulkSendingInvitations}
                  data-testid="bulk-send-invitations-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {bulkSendingInvitations ? 'Sending...' : 'Send All Invitations'}
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => isSubscriptionExpired ? toast.error('Subscribe to add new employees') : setCreateEmployeeModalOpen(true)}
                disabled={isSubscriptionExpired}
                data-testid="create-employee-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('people.directCreate')}
              </Button>
            </>
          )}
          
          {!isFullStealthPolicy && (
            <Button
              onClick={() => isSubscriptionExpired ? toast.error('Subscribe to invite new employees') : setInviteModalOpen(true)}
              disabled={isSubscriptionExpired}
              data-testid="invite-employee-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('people.inviteByEmail')}
            </Button>
          )}
          {isFullStealthPolicy && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
              Full Stealth Mode active — email invitations are disabled. Use <strong>Add Employee</strong> + Device Token.
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:border-blue-500/50 transition-colors"
          onClick={() => setStatusFilter('all')}
          data-testid="stat-total-employees"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_employees}</p>
                <p className="text-sm text-slate-500">Total Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-emerald-500/50 transition-colors"
          data-testid="stat-active-now"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                <Activity className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.active_now}</p>
                <p className="text-sm text-slate-500">Active Now</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-amber-500/50 transition-colors"
          data-testid="stat-pending-invites"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.pending_invites}</p>
                <p className="text-sm text-slate-500">Pending Invites</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-purple-500/50 transition-colors"
          onClick={() => setDepartmentModalOpen(true)}
          data-testid="stat-departments"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.departments}</p>
                <p className="text-sm text-slate-500">Departments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('people.search')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {!isFreePlan && (
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[130px]" data-testid="role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
              )}

              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[150px]" data-testid="department-filter">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending_setup">Pending Setup</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="employees" className="space-y-4">
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">
            Employees ({employees.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">
            Pending Invitations ({invitations.length})
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="employees-table">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <SortHeader column="name" label="Name" />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common.email')}</th>
                      {!isFreePlan && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('people.role')}</th>}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('people.status')}</th>
                      <SortHeader column="date_joined" label="Joined" />
                      <SortHeader column="last_active" label="Last Active" />
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {employees.map((emp) => {
                      const lastActive = getLastActiveLabel(emp.last_tracked);
                      return (
                        <tr 
                          key={emp.id} 
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                          onClick={() => openEditModal(emp.id)}
                          data-testid={`employee-row-${emp.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm overflow-hidden">
                                {emp.profile_photo ? (
                                  <img src={emp.profile_photo} alt={emp.name} className="w-full h-full object-cover" />
                                ) : (
                                  emp.name?.charAt(0).toUpperCase()
                                )}
                              </div>
                              <span className="font-medium text-slate-900 dark:text-white">{emp.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{emp.email}</td>
                          {!isFreePlan && (
                          <td className="px-4 py-3">
                            <Badge className={getRoleBadgeColor(emp.role)}>
                              {emp.role?.charAt(0).toUpperCase() + emp.role?.slice(1)}
                            </Badge>
                          </td>
                          )}
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{emp.department || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge 
                              variant={emp.account_status === 'active' ? 'default' : 'secondary'}
                              className={emp.account_status === 'pending_setup' ? 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100' : ''}
                            >
                              {emp.account_status === 'active' ? 'Active' : emp.account_status === 'pending_setup' ? 'Pending Setup' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {formatDate(emp.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${lastActive.color}`}>{lastActive.text}</span>
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`employee-actions-${emp.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {/* View Profile - available to both admin and manager */}
                                <DropdownMenuItem onClick={() => openEditModal(emp.id)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  View Profile
                                </DropdownMenuItem>
                                
                                {/* Admin-only actions */}
                                {user?.role === 'admin' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {emp.account_status === 'pending_setup' && !isFullStealthPolicy && (
                                      <DropdownMenuItem
                                        onClick={() => handleSendInvitation(emp.id)}
                                        disabled={sendingInvitation === emp.id}
                                        className="text-blue-600"
                                        data-testid={`send-invitation-${emp.id}`}
                                      >
                                        <Mail className="w-4 h-4 mr-2" />
                                        {sendingInvitation === emp.id ? 'Sending...' : 'Send Invitation'}
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedEmployee(emp);
                                        setResetPasswordForm('');
                                        setResetPasswordModalOpen(true);
                                      }}
                                      data-testid={`reset-password-${emp.id}`}
                                    >
                                      <KeyRound className="w-4 h-4 mr-2" />
                                      Reset Password
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleGenerateDeviceToken(emp)}
                                      data-testid={`generate-device-token-${emp.id}`}
                                    >
                                      <Smartphone className="w-4 h-4 mr-2" />
                                      Generate Device Token
                                    </DropdownMenuItem>
                                    {emp.account_status === 'active' ? (
                                      <DropdownMenuItem 
                                        className="text-red-600"
                                        onClick={() => {
                                          setSelectedEmployee(emp);
                                          setDeactivateModalOpen(true);
                                        }}
                                      >
                                        <UserX className="w-4 h-4 mr-2" />
                                        Deactivate
                                      </DropdownMenuItem>
                                    ) : emp.account_status === 'inactive' ? (
                                      <DropdownMenuItem 
                                        className="text-emerald-600"
                                        onClick={() => handleReactivate(emp.id)}
                                      >
                                        <UserCheck className="w-4 h-4 mr-2" />
                                        Reactivate
                                      </DropdownMenuItem>
                                    ) : null}
                                    <DropdownMenuItem 
                                      className="text-red-600"
                                      onClick={() => {
                                        setSelectedEmployee(emp);
                                        setDeleteModalOpen(true);
                                      }}
                                      data-testid={`delete-employee-${emp.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                    {employees.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={pagination.page === pagination.total_pages}
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="invitations-table">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                      {!isFreePlan && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</th>}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Invited By</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sent</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {invitations.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`invitation-row-${inv.id}`}>
                        <td className="px-4 py-3 text-slate-900 dark:text-white">{inv.email}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.name || '-'}</td>
                        {!isFreePlan && (
                        <td className="px-4 py-3">
                          <Badge className={getRoleBadgeColor(inv.role)}>
                            {inv.role?.charAt(0).toUpperCase() + inv.role?.slice(1)}
                          </Badge>
                        </td>
                        )}
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.department || '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.invited_by_name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                          {formatDate(inv.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleResendInvite(inv.id)}
                              data-testid={`resend-invite-${inv.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Resend
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleCancelInvite(inv.id)}
                              data-testid={`cancel-invite-${inv.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {invitations.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                          No pending invitations
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <InviteDialog
        open={inviteModalOpen} onOpenChange={setInviteModalOpen}
        inviteForm={inviteForm} setInviteForm={setInviteForm}
        onInvite={handleInvite} departments={departments} userRole={user?.role}
        isFreePlan={isFreePlan}
      />
      <BulkInviteDialog
        open={bulkInviteModalOpen} onOpenChange={setBulkInviteModalOpen}
        bulkInvites={bulkInvites} setBulkInvites={setBulkInvites}
        onBulkInvite={handleBulkInvite} onDownloadTemplate={downloadCsvTemplate} onUpload={handleBulkInviteUpload}
      />
      <EditEmployeeDialog
        open={editModalOpen} onOpenChange={setEditModalOpen}
        employee={selectedEmployee} setEmployee={setSelectedEmployee}
        departments={departments} employees={employees} orgLeaveTypes={orgLeaveTypes}
        userRole={user?.role} onSave={handleSaveEmployee}
        isFreePlan={isFreePlan} stealthEnabled={stealthEnabled}
        onGenerateDeviceToken={handleGenerateDeviceToken}
      />
      <DeactivateDialog
        open={deactivateModalOpen} onOpenChange={setDeactivateModalOpen}
        employeeName={selectedEmployee?.name} onDeactivate={handleDeactivate}
      />
      <DeleteEmployeeDialog
        open={deleteModalOpen} onOpenChange={setDeleteModalOpen}
        employeeName={selectedEmployee?.name} onDelete={handleDeleteEmployee}
      />
      <DepartmentDialog
        open={departmentModalOpen} onOpenChange={setDepartmentModalOpen}
        departments={departments} departmentForm={departmentForm} setDepartmentForm={setDepartmentForm}
        departmentFormError={departmentFormError} setDepartmentFormError={setDepartmentFormError}
        editingDepartment={editingDepartment} setEditingDepartment={setEditingDepartment}
        onSave={handleSaveDepartment} onDelete={handleDeleteDepartment} userRole={user?.role}
      />
      <CreateEmployeeDialog
        open={createEmployeeModalOpen} onOpenChange={setCreateEmployeeModalOpen}
        form={createEmployeeForm} setForm={setCreateEmployeeForm}
        departments={departments} onSubmit={handleCreateEmployee}
        loading={createEmployeeLoading} onGeneratePassword={generatePassword}
        isFreePlan={isFreePlan}
        isFullStealthPolicy={isFullStealthPolicy}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new temporary password for <strong>{selectedEmployee?.name}</strong>. They will be prompted to change it on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reset-new-password">New Temporary Password</Label>
              <div className="flex gap-2">
                <Input
                  id="reset-new-password"
                  data-testid="reset-password-input"
                  type="text"
                  placeholder="Enter new password (min 6 chars)"
                  value={resetPasswordForm}
                  onChange={(e) => setResetPasswordForm(e.target.value)}
                />
                <Button type="button" variant="outline" size="sm" onClick={generateResetPassword} data-testid="generate-reset-password-btn">
                  Generate
                </Button>
              </div>
              {resetPasswordForm && resetPasswordForm.length < 6 && (
                <p className="text-xs text-red-500">Password must be at least 6 characters</p>
              )}
            </div>
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                The employee will receive an email with the new password. Make sure to verify the email is correct: <strong>{selectedEmployee?.email}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(false)} data-testid="reset-password-cancel-btn">Cancel</Button>
            <Button
              onClick={handleResetPassword}
              disabled={!resetPasswordForm || resetPasswordForm.length < 6 || resetPasswordLoading}
              data-testid="reset-password-confirm-btn"
            >
              {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Employees Dialog - Admin only */}
      <ImportEmployeesDialog
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        token={token}
        onImportComplete={() => { setStatusFilter('all'); fetchEmployees(); fetchStats(); }}
      />

      {/* Device Token Display Dialog */}
      <Dialog open={!!deviceTokenModal} onOpenChange={(o) => !o && setDeviceTokenModal(null)}>
        <DialogContent data-testid="device-token-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> Device Enrollment Token
            </DialogTitle>
            <DialogDescription>
              {deviceTokenModal && (
                <>For <strong>{deviceTokenModal.employee_name}</strong> ({deviceTokenModal.employee_email}).
                Enter this token in the InspiroTrack desktop app on the employee&apos;s laptop. The token is single-use and valid for 48 hours.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {deviceTokenModal && (
            <div className="space-y-4">
              <div className="bg-slate-900 text-white rounded-lg p-6 text-center font-mono text-2xl tracking-widest break-all select-all" data-testid="device-token-value">
                {deviceTokenModal.token}
              </div>
              <div className="text-xs text-slate-500 text-center">
                Expires {new Date(deviceTokenModal.expires_at).toLocaleString()}
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
                <strong>Important:</strong> Copy this token now. Once you close this dialog you will need to generate a new token.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => {
                if (deviceTokenModal?.token) {
                  try {
                    await navigator.clipboard.writeText(deviceTokenModal.token);
                    toast.success('Token copied to clipboard');
                  } catch {
                    toast.error('Copy failed — please select the token manually');
                  }
                }
              }}
              data-testid="copy-device-token-btn"
            >
              Copy Token
            </Button>
            <Button onClick={() => setDeviceTokenModal(null)} data-testid="close-device-token-modal">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
